/**
 * Qinggong in the bout (duel v3 P1.5 a, b, c, f). Bout only (`DUEL_FLAGS.v2`, a fight under way); the open world keeps its
 * own steps, jumps and chains.
 * - A step's i-frames are its own length plus a little (not the 0.35-0.5 s of the open world), then a short stop in which
 *   the fighter cannot guard, step, jump or press a move (`duelRecovery.startStop`): the punish window.
 * - Direction means something, relative to the foe: back = full distance, short stop; side = 80% of it, curled round the
 *   foe (still facing it), shortest stop; forward = stops 1 m short of the foe, longest stop. A second step inside 1.5 s
 *   costs x1.5 พลังกาย (no step spam).
 * - A landing from a jump has a stop (unless it ended a dive that landed: duelAir).
 * - สลัดหลุด: Q in hit-stun from ordinary hits (not a broken guard, not a lock form) for พลังกาย is a back-step with
 *   i-frames, then no step at all for a while. No damage and no counter blow.
 */
import { COMBAT } from '../combat';
import { DUEL_FLAGS, DUEL_MOVE } from '../data/duelMatch';
import type { Entity, PlayerCommand, WorldEvent } from '../world';
import { duelLockOf } from './duelLock';
import { duelMatchOf } from './duelMatch';
import { duelRecoveryOf } from './duelRecovery';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelMoveSystem>();
export const duelMoveOf = (w: object): DuelMoveSystem | null => SYSTEMS.get(w) ?? null;

export type StepKind = 'back' | 'side' | 'fwd' | 'air' | 'escape';

export class DuelMoveSystem implements WorldSystem {
  readonly name = 'duelMove';
  private readonly last = new Map<number, number>();
  private readonly lockUntil = new Map<number, number>();
  private readonly pend = new Map<number, { kind: StepKind; stop: number }>();
  /** The latest step of each fighter (for the picture: afterimages count, pose). */
  private readonly lastStep = new Map<number, { kind: StepKind; at: number }>();
  private readonly wasAir = new Set<number>();
  private escaping = false;
  /** Fighters whose air-time ended this tick in a dive that landed (no landing stop). */
  readonly skipLanding = new Set<number>();

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** The kind and world time of a fighter's latest step (null: none yet in this fight). */
  latest(id: number): { kind: StepKind; at: number } | null {
    return this.lastStep.get(id) ?? null;
  }

  /** Seconds of dodge lock left after an escape. */
  lockLeft(id: number): number {
    return Math.max(0, (this.lockUntil.get(id) ?? 0) - this.h.world.time);
  }

  inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  foeOf(id: number): Entity | undefined {
    const b = this.h.world.pvpBout;
    if (!b) return undefined;
    return this.h.world.get(b.a === id ? b.b : b.a);
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'dash') return false;
    const w = this.h.world;
    const p = w.player;
    if (!this.inBout(p.id)) return false;
    if (p.dead) return false;
    if (this.lockLeft(p.id) > 0) return true;
    if (p.stunT > 0) return this.escape(p);
    if (p.dashT > 0 || p.cast || p.spinT > 0) return false;
    const d = w.qgStats().dash;
    const air = p.z > 0;
    if (air) return false;
    const lastAt = this.last.get(p.id);
    if (lastAt !== undefined && w.time - lastAt < DUEL_MOVE.spamWindow && p.stamina < d.stamina * DUEL_MOVE.spamMult) {
      this.h.notice('พลังกายไม่พอ');
      return true;
    }
    return false;
  }

  observe(e: WorldEvent): void {
    if (e.type !== 'dash' || this.escaping || !this.inBout(e.id)) return;
    const w = this.h.world;
    const me = w.get(e.id);
    if (!me) return;
    const foe = this.foeOf(e.id);
    const air = me.z > 0;
    const sp = Math.hypot(me.dashVX, me.dashVY);
    let kind: StepKind = 'air';
    if (foe && sp > 0 && !air) {
      const fx = foe.x - me.x;
      const fy = foe.y - me.y;
      const fd = Math.hypot(fx, fy) || 1;
      const ux = fx / fd;
      const uy = fy / fd;
      const dot = (me.dashVX * ux + me.dashVY * uy) / sp;
      const len = sp * COMBAT.dashDuration;
      if (dot < -0.5) kind = 'back';
      else if (dot > 0.5) {
        kind = 'fwd';
        const k = Math.min(len, Math.max(0, fd - DUEL_MOVE.fwdGap)) / len;
        me.dashVX *= k;
        me.dashVY *= k;
      } else {
        kind = 'side';
        const s = Math.sign(me.dashVX * -uy + me.dashVY * ux) || 1;
        const v = (len * DUEL_MOVE.sideDist) / COMBAT.dashDuration;
        me.dashVX = -uy * s * v;
        me.dashVY = ux * s * v;
      }
    }
    me.invulnT = COMBAT.dashDuration + DUEL_MOVE.invulnPad;
    const lastAt = this.last.get(e.id);
    if (lastAt !== undefined && w.time - lastAt < DUEL_MOVE.spamWindow)
      me.stamina = Math.max(0, me.stamina - w.qgStats().dash.stamina * (DUEL_MOVE.spamMult - 1));
    this.last.set(e.id, w.time);
    this.lastStep.set(e.id, { kind, at: w.time });
    if (!air && kind !== 'air') this.pend.set(e.id, { kind, stop: DUEL_MOVE.stop[kind as 'back' | 'side' | 'fwd'] });
  }

  /** สลัดหลุด: true when the press was taken (done, or refused for a reason the player is told). */
  private escape(p: Entity): boolean {
    const w = this.h.world;
    if (duelLockOf(w)?.inForm(p.id)) return false;
    if ((duelMatchOf(w)?.brokenLeft(p.id) ?? 0) > 0) return false;
    if (p.stamina < DUEL_MOVE.escape.cost) {
      this.h.notice('พลังกายไม่พอสลัดหลุด');
      return true;
    }
    const foe = this.foeOf(p.id);
    let ux = p.dirX;
    let uy = p.dirY;
    if (foe) {
      ux = foe.x - p.x;
      uy = foe.y - p.y;
    }
    const l = Math.hypot(ux, uy) || 1;
    p.stamina -= DUEL_MOVE.escape.cost;
    p.stunT = 0;
    p.dashT = COMBAT.dashDuration;
    p.dashVX = (-ux / l) * (DUEL_MOVE.escape.dist / COMBAT.dashDuration);
    p.dashVY = (-uy / l) * (DUEL_MOVE.escape.dist / COMBAT.dashDuration);
    p.invulnT = COMBAT.dashDuration + DUEL_MOVE.invulnPad;
    p.blocking = false;
    p.path = null;
    this.lockUntil.set(p.id, w.time + DUEL_MOVE.escape.lock);
    this.last.set(p.id, w.time);
    this.lastStep.set(p.id, { kind: 'escape', at: w.time });
    if (p.z <= 0) this.pend.set(p.id, { kind: 'escape', stop: DUEL_MOVE.escape.stop });
    this.escaping = true;
    this.h.emit({ type: 'dash', id: p.id });
    this.escaping = false;
    return true;
  }

  step(_dt: number): void {
    const w = this.h.world;
    if (!w.pvpBout?.fight || !DUEL_FLAGS.v2) {
      this.last.clear();
      this.lockUntil.clear();
      this.pend.clear();
      this.lastStep.clear();
      this.wasAir.clear();
      this.skipLanding.clear();
      return;
    }
    const rec = duelRecoveryOf(w);
    for (const [id, p] of this.pend) {
      const e = w.get(id);
      if (!e || e.dead) this.pend.delete(id);
      else if (e.dashT <= 0) {
        this.pend.delete(id);
        // A step ends facing the foe (the figure is drawn facing it through the step), never with the back turned.
        const foe = w.get(id === w.pvpBout.a ? w.pvpBout.b : w.pvpBout.a);
        if (foe) this.h.face(e, foe);
        rec?.startStop(id, p.stop, `step:${p.kind}`);
      }
    }
    // Landings: air-time that ends in a stop, unless a dive that landed says otherwise.
    for (const id of [w.pvpBout.a, w.pvpBout.b]) {
      const e = w.get(id);
      if (!e) continue;
      const air = e.z > 0;
      if (this.wasAir.has(id) && !air) {
        if (!this.skipLanding.has(id) && e.stunT <= 0) rec?.startStop(id, DUEL_MOVE.landStop, 'land');
      }
      if (air) this.wasAir.add(id);
      else this.wasAir.delete(id);
    }
    this.skipLanding.clear();
  }
}
